import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { NewsArticleFactCheck, NewsArticleFactCheckVerdict } from '@/services/newsroomApi';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';

const VERDICT_META: Record<NewsArticleFactCheckVerdict, { icon: React.ElementType; className: string; label: string }> = {
  corroborated: { icon: CheckCircleIcon, className: 'text-green-600 dark:text-green-400', label: 'Corroborated' },
  contradicted: { icon: ExclamationTriangleIcon, className: 'text-red-600 dark:text-red-400', label: 'Contradicted' },
  unverified: { icon: QuestionMarkCircleIcon, className: 'text-gray-400 dark:text-gray-500', label: 'Unverified' },
};

interface FactCheckPanelProps {
  factCheck?: NewsArticleFactCheck;
  editorNotes: string;
  onEditorNotesChange: (value: string) => void;
  onRerun: () => void;
  isRerunning: boolean;
}

// Advisory cross-reference display — never blocks the review/publish
// workflow, matches the backend's "advisory only" design.
const FactCheckPanel: React.FC<FactCheckPanelProps> = ({ factCheck, editorNotes, onEditorNotesChange, onRerun, isRerunning }) => {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Fact Check (cross-reference)</h3>
        <button
          onClick={onRerun}
          disabled={isRerunning}
          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
        >
          {isRerunning ? 'Running…' : 'Re-run'}
        </button>
      </div>

      {!factCheck || factCheck.status === 'not_run' ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Not run yet.</p>
      ) : factCheck.status === 'failed' ? (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Fact-check failed last run — this is advisory only and doesn't block publishing.
        </p>
      ) : factCheck.claims.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No claims extracted.</p>
      ) : (
        <ul className="space-y-3">
          {factCheck.claims.map((claim, idx) => {
            const meta = VERDICT_META[claim.verdict];
            const Icon = meta.icon;
            return (
              <li key={idx} className="flex gap-2">
                <Icon className={`h-5 w-5 flex-shrink-0 ${meta.className}`} />
                <div>
                  <p className="text-sm text-gray-900 dark:text-white">{claim.claim}</p>
                  <p className={`text-xs ${meta.className}`}>
                    {meta.label}
                    {claim.note ? ` — ${claim.note}` : ''}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Editor notes</label>
        <LocalizedTextArea
          value={editorNotes}
          onChange={(e) => onEditorNotesChange(e.target.value)}
          rows={3}
          maxLength={2000}
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white p-2"
          placeholder="Add your own fact-check notes or overrides…"
        />
      </div>
    </div>
  );
};

export default FactCheckPanel;
